import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import SEO from "../components/SEO";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../components/ui/card";
import {
  ChevronRight, Loader2, CheckCircle, Copy, Mail, LinkIcon, AlertCircle,
} from "lucide-react";

// A minimal, mobile-first guest form. It intentionally keeps required
// fields lean (name, contact, service, rego OR tyre size, address, radius,
// note) so drivers can submit in under 2 minutes without creating an
// account. The magic-link URL returned on submit lets them reopen the
// same comparison page as quotes arrive.

const SERVICES = [
  { id: "tyres", label: "Tyres" },
  { id: "alignment", label: "Wheel Alignment" },
  { id: "rotation", label: "Tyre Rotation" },
];

export default function GuestQuoteRequest() {
  const navigate = useNavigate();

  const [selectedServices, setSelectedServices] = useState([]);
  const [rego, setRego] = useState("");
  const [tyreSize, setTyreSize] = useState({ width: "", aspect: "", rim: "" });
  const [quantity, setQuantity] = useState(4);
  const [address, setAddress] = useState(null);
  const [radius, setRadius] = useState(25);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [note, setNote] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {access_url, request_number, ...}

  const needsTyres = selectedServices.includes("tyres");

  const toggleService = (id) => {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const canSubmit = () => {
    if (selectedServices.length === 0) return false;
    if (!name.trim()) return false;
    if (!email.trim() && !mobile.trim()) return false;
    if (!address || !address.full_address) return false;
    if (!note.trim()) return false;
    if (needsTyres) {
      const hasRego = rego.trim().length > 0;
      const hasSize = tyreSize.width && tyreSize.aspect && tyreSize.rim;
      // Backend enforces both rego + size for tyre services; keep the same
      // rule so guests get the same quote quality as logged-in customers.
      if (!hasRego || !hasSize) return false;
    } else {
      // Non-tyre services still need at least a rego or size for shop context
      const hasRego = rego.trim().length > 0;
      const hasSize = tyreSize.width && tyreSize.aspect && tyreSize.rim;
      if (!hasRego && !hasSize) return false;
    }
    return true;
  };

  const submit = async () => {
    if (!canSubmit()) {
      toast.error("Please complete every required field before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const service_type = needsTyres
        ? (selectedServices.includes("alignment") ? "tyres_alignment" : "tyres_only")
        : selectedServices.includes("alignment") ? "alignment_only" : "rotation_only";

      const payload = {
        request_type: "standard",
        service_type,
        selected_services: selectedServices,
        rego: rego.trim() || undefined,
        tyre_specs: needsTyres || (tyreSize.width && tyreSize.aspect && tyreSize.rim)
          ? { width: tyreSize.width, aspect_ratio: tyreSize.aspect, rim_size: tyreSize.rim }
          : undefined,
        search_radius: radius,
        address,
        quantity: needsTyres ? quantity : 4,
        customer_note: note.trim(),
        guest_name: name.trim(),
        guest_email: email.trim() || undefined,
        guest_mobile: mobile.trim() || undefined,
      };

      const res = await axios.post(`${API}/public/guest-quote-request`, payload);
      setResult(res.data);
      toast.success("Request submitted! Save your access link.");
    } catch (e) {
      const detail = e.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    if (!result?.access_url) return;
    navigator.clipboard.writeText(result.access_url).then(
      () => toast.success("Link copied to clipboard"),
      () => toast.error("Copy failed — please copy manually."),
    );
  };

  // Success screen — takes over the whole page.
  if (result) {
    return (
      <div className="min-h-screen bg-background">
        <SEO page="quoteRequest" />
        <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-30">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            <Link to="/" className="font-heading font-bold text-lg text-primary" data-testid="guest-home-link">
              TyreMatch
            </Link>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold" data-testid="guest-success-title">
                Request submitted
              </h1>
              <p className="text-sm text-muted-foreground">
                Reference {result.request_number || `#${result.request_id}`}
              </p>
            </div>
          </div>

          <Card className="border-2 border-primary/40 bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <LinkIcon className="h-4 w-4 text-primary" />
                Save this link — quotes arrive over hours or days
              </CardTitle>
              <CardDescription>
                This same link always shows the latest quotes. Bookmark it, or use the copy button.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2 items-stretch">
                <Input
                  readOnly
                  value={result.access_url}
                  className="font-mono text-xs sm:text-sm"
                  data-testid="guest-access-url-input"
                  onFocus={(e) => e.target.select()}
                />
                <Button onClick={copyLink} variant="secondary" data-testid="guest-copy-url-btn">
                  <Copy className="h-4 w-4 mr-1" /> Copy
                </Button>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => navigate(`/r/${result.access_token}`)}
                  className="w-full sm:w-auto"
                  data-testid="guest-open-comparison-btn"
                >
                  Open my quote page
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
                {email && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 sm:ml-auto">
                    <Mail className="h-4 w-4" /> Sent to {email}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="rounded-lg border p-4 bg-muted/30 text-sm text-muted-foreground">
            <p className="mb-2 font-medium text-foreground flex items-center gap-1">
              <AlertCircle className="h-4 w-4 text-primary" /> What happens next
            </p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Nearby shops within {radius} km can see your request and submit private quotes.</li>
              <li>Shops can't see each other's quotes — you'll compare privately.</li>
              <li>You'll get an email each time a new quote arrives (with this same link).</li>
              <li>Payment happens at the shop when you book fitment.</li>
            </ul>
          </div>
        </main>
      </div>
    );
  }

  // Form
  return (
    <div className="min-h-screen bg-background">
      <SEO page="quoteRequest" />
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-heading font-bold text-lg text-primary">TyreMatch</Link>
          <div className="text-xs sm:text-sm">
            <Link to="/customer/auth?tab=login" className="text-muted-foreground hover:text-primary" data-testid="guest-login-link">
              Have an account? Log in
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2" data-testid="guest-form-title">
            Get private tyre quotes
          </h1>
          <p className="text-muted-foreground text-sm">
            No account needed. Local NZ shops will send private quotes — you'll get a personal link to compare them.
          </p>
        </div>

        {/* Services */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">What service(s) do you need?</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {SERVICES.map((s) => (
              <label
                key={s.id}
                className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer ${
                  selectedServices.includes(s.id) ? "border-primary bg-primary/10" : "hover:border-primary/40"
                }`}
                data-testid={`guest-service-${s.id}`}
              >
                <Checkbox
                  checked={selectedServices.includes(s.id)}
                  onCheckedChange={() => toggleService(s.id)}
                />
                <span className="text-sm font-medium">{s.label}</span>
              </label>
            ))}
          </CardContent>
        </Card>

        {/* Vehicle */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Vehicle</CardTitle>
            <CardDescription className="text-xs">
              {needsTyres
                ? "For tyres we need BOTH your rego and tyre size (max 6 alphanumeric chars for rego)."
                : "Enter your rego OR tyre size so shops can quote accurately."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="guest-rego">Rego</Label>
              <Input
                id="guest-rego"
                value={rego}
                maxLength={6}
                onChange={(e) => setRego(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                placeholder="ABC123"
                className="mt-1"
                data-testid="guest-rego-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor="tw">Width</Label>
                <Input id="tw" placeholder="205" value={tyreSize.width} onChange={(e) => setTyreSize({ ...tyreSize, width: e.target.value })} data-testid="guest-tyre-width" />
              </div>
              <div>
                <Label htmlFor="ta">Profile</Label>
                <Input id="ta" placeholder="55" value={tyreSize.aspect} onChange={(e) => setTyreSize({ ...tyreSize, aspect: e.target.value })} data-testid="guest-tyre-aspect" />
              </div>
              <div>
                <Label htmlFor="tr">Rim</Label>
                <Input id="tr" placeholder="16" value={tyreSize.rim} onChange={(e) => setTyreSize({ ...tyreSize, rim: e.target.value })} data-testid="guest-tyre-rim" />
              </div>
            </div>
            {needsTyres && (
              <div>
                <Label>How many tyres?</Label>
                <div className="flex items-center gap-2 mt-1">
                  {[2, 4].map((q) => (
                    <Button
                      key={q}
                      type="button"
                      variant={quantity === q ? "default" : "outline"}
                      onClick={() => setQuantity(q)}
                      data-testid={`guest-quantity-${q}`}
                    >
                      {q}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Location */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Where should we search?</CardTitle>
            <CardDescription className="text-xs">Your suburb/address matches you with nearby shops.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <AddressAutocomplete value={address} onChange={setAddress} placeholder="Start typing your address..." required />
            <div>
              <Label>Search radius: {radius} km</Label>
              <Slider value={[radius]} onValueChange={(v) => setRadius(v[0])} min={5} max={50} step={5} className="mt-2" data-testid="guest-radius-slider" />
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">How can shops reach you?</CardTitle>
            <CardDescription className="text-xs">Email or mobile — at least one is required. We'll email your quote link here.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="gn">Your name</Label>
              <Input id="gn" value={name} onChange={(e) => setName(e.target.value)} placeholder="First name" data-testid="guest-name-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label htmlFor="ge">Email</Label>
                <Input id="ge" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="guest-email-input" />
              </div>
              <div>
                <Label htmlFor="gm">Mobile</Label>
                <Input id="gm" type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="021 123 4567" data-testid="guest-mobile-input" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Note */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Note for shops</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="E.g. Prefer Bridgestone, need it by Friday, etc." rows={3} data-testid="guest-note-input" />
          </CardContent>
        </Card>

        <div className="sticky bottom-0 -mx-4 sm:mx-0 bg-background/95 backdrop-blur px-4 py-4 border-t sm:border-0 sm:static">
          <Button
            size="lg"
            className="w-full h-12 text-base rounded-full"
            disabled={!canSubmit() || submitting}
            onClick={submit}
            data-testid="guest-submit-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {submitting ? "Submitting..." : "Get my quotes"}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-2">
            No password required. You'll get a private link to compare quotes.
          </p>
        </div>
      </main>
    </div>
  );
}
